import type { TuitionMonthlyFee } from '@/types';

/** 오늘 날짜를 로컬 기준(YYYY-MM-DD) 문자열로 반환한다. */
export function todayKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export type MonthlyFeeEntry = Pick<TuitionMonthlyFee, 'year' | 'month' | 'fee'> & {
  is_excluded?: boolean;
};

/**
 * 특정 연·월에 해당하는 교습비를 구한다.
 * 그 달에 직접 입력한 월별 교습비가 있으면 그 값을, 없으면 기본 금액을 돌려준다.
 */
export function monthlyFeeAt(
  monthlyFees: MonthlyFeeEntry[],
  fallbackFee: number,
  year: number,
  month: number,
): number {
  const found = monthlyFees.find((f) => f.year === year && f.month === month);
  return found ? found.fee : fallbackFee;
}

/** 날짜(YYYY-MM-DD)가 속한 달의 교습비를 구한다. */
export function monthlyFeeAtDate(
  monthlyFees: MonthlyFeeEntry[],
  fallbackFee: number,
  dateKey: string,
): number {
  const [y, m] = dateKey.slice(0, 7).split('-').map(Number);
  return monthlyFeeAt(monthlyFees, fallbackFee, y, m);
}

/** 특정 연·월 회차가 '제외(휴원·면제)' 되었는지 확인한다. */
export function isCycleExcluded(
  monthlyFees: MonthlyFeeEntry[],
  year: number,
  month: number,
): boolean {
  const found = monthlyFees.find((f) => f.year === year && f.month === month);
  return Boolean(found?.is_excluded);
}

/** 제외된 회차 키(YYYY-MM) 목록을 뽑는다. */
export function excludedCycleKeysOf(monthlyFees: MonthlyFeeEntry[]): string[] {
  return monthlyFees.filter((f) => f.is_excluded).map((f) => cycleKey(f.year, f.month));
}

/* ============================================================
 * 결제 회차(월) 계산
 * 교습비는 매월 결제일(dueDay)에 한 회차씩 발생한다.
 * "이번 회차"·"다음 결제일"은 단순 달력이 아니라 실제 납부 기록
 * (tuition_payments.cycle_due)을 기준으로 계산해서, 다음 달분을
 * 미리 낸 경우에도 다음 결제금액이 제대로 나오게 한다.
 * ============================================================ */

/** 연·월을 "YYYY-MM" 회차 키로 만든다. */
export function cycleKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/** "YYYY-MM" 회차 키를 { year, month }로 분해한다. */
export function parseCycleKey(key: string): { year: number; month: number } {
  const [y, m] = key.split('-').map(Number);
  return { year: y, month: m };
}

/**
 * 납부 기록의 cycle_due(YYYY-MM-DD)에서 회차 키(YYYY-MM)를 만든다.
 * 결제일이 '전월(monthOffset=-1)'이면 cycle_due는 회차보다 한 달 앞선 날짜이므로,
 * 그만큼 되돌려 원래 회차 키를 복원한다.
 */
export function cycleKeyOfDate(dateKey: string, monthOffset = 0): string {
  return addCycleMonths(dateKey.slice(0, 7), -monthOffset);
}

/** "YYYY-MM" 회차 키에 개월 수를 더한다. */
export function addCycleMonths(key: string, delta: number): string {
  const { year, month } = parseCycleKey(key);
  const total = year * 12 + (month - 1) + delta;
  const y = Math.floor(total / 12);
  const m = (((total % 12) + 12) % 12) + 1;
  return cycleKey(y, m);
}

/** "2026-09" → "2026년 9월" */
export function cycleMonthLabel(key: string): string {
  const { year, month } = parseCycleKey(key);
  return `${year}년 ${month}월`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/**
 * 회차 키(YYYY-MM)와 결제일로 그 회차의 결제 예정일(YYYY-MM-DD)을 만든다.
 * monthOffset=-1 이면 '전월 결제'로, 결제 예정일이 회차보다 한 달 앞당겨진다.
 *   예) 2026-10 회차 + 결제일 22 + offset -1 → 2026-09-22
 */
export function cycleDueDate(key: string, dueDay: number, monthOffset = 0): string {
  const { year, month } = parseCycleKey(addCycleMonths(key, monthOffset));
  const day = Math.max(1, Math.min(dueDay, daysInMonth(year, month)));
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** 결제일을 사람이 읽기 좋은 문구로 만든다. (예: '매월 22일' / '전월 22일') */
export function dueDayLabel(dueDay?: number | null, monthOffset = 0): string {
  if (!dueDay) return '미입력';
  return monthOffset < 0 ? `전월 ${dueDay}일` : `매월 ${dueDay}일`;
}

/** 회차별 납부 상태. */
export type CyclePaymentStatus = 'paid' | 'overdue' | 'upcoming' | 'excluded';

export interface ResolvedCycles {
  /** 이번 회차(달력 기준). 미납/예정 판정의 기준이 된다. */
  anchorKey: string;
  /** 지금 받아야 할 회차 = 가장 이른 미납 회차. (미리 낸 달은 자동으로 건너뜀) */
  collectKey: string;
  /** 다음 결제 예정 회차(납부 완료한 달은 건너뜀). */
  nextDueKey: string;
  /** 이번 회차 상태. */
  status: CyclePaymentStatus;
}

const MAX_CYCLE_SCAN = 36;

function parseLocalDate(dateKey: string): Date {
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * 납부 기록과 '제외 회차'를 기준으로 결제 회차 정보를 계산한다.
 * @param dueDay 매월 결제일(1~31)
 * @param paidCycleKeys 이미 납부 완료한 회차 키 목록(YYYY-MM)
 * @param excludedCycleKeys 휴원·면제로 건너뛸 회차 키 목록(YYYY-MM)
 * @param monthOffset 결제 기준 개월 오프셋(0=당월, -1=전월)
 */
export function resolveTuitionCycles(
  dueDay: number | null | undefined,
  paidCycleKeys: string[],
  excludedCycleKeys: string[] = [],
  monthOffset = 0,
  today: Date = new Date(),
): ResolvedCycles | null {
  if (!dueDay || dueDay < 1 || dueDay > 31) return null;

  const paid = new Set(paidCycleKeys);
  const excluded = new Set(excludedCycleKeys);
  const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const thisMonthKey = cycleKey(todayMid.getFullYear(), todayMid.getMonth() + 1);

  const isDueOnOrBefore = (key: string) =>
    parseLocalDate(cycleDueDate(key, dueDay, monthOffset)).getTime() <= todayMid.getTime();

  // 이번 회차: 결제일이 이미 지나간 가장 최근 회차
  let anchorKey = thisMonthKey;
  for (let i = 0; i < MAX_CYCLE_SCAN && !isDueOnOrBefore(anchorKey); i += 1) {
    anchorKey = addCycleMonths(anchorKey, -1);
  }
  for (let i = 0; i < MAX_CYCLE_SCAN && isDueOnOrBefore(addCycleMonths(anchorKey, 1)); i += 1) {
    anchorKey = addCycleMonths(anchorKey, 1);
  }
  const anchorDue = parseLocalDate(cycleDueDate(anchorKey, dueDay, monthOffset));

  let status: CyclePaymentStatus;
  if (paid.has(anchorKey)) {
    status = 'paid';
  } else if (excluded.has(anchorKey)) {
    status = 'excluded';
  } else if (anchorDue.getTime() < todayMid.getTime()) {
    status = 'overdue';
  } else {
    status = 'upcoming';
  }

  // 지금 받아야 할 회차: 이번 회차부터 앞으로 훑어 납부·제외된 회차를 건너뛴 가장 이른 회차
  let collectKey = anchorKey;
  for (let i = 0; i < MAX_CYCLE_SCAN && (paid.has(collectKey) || excluded.has(collectKey)); i += 1) {
    collectKey = addCycleMonths(collectKey, 1);
  }

  // 다음 결제 예정 회차: 이번 회차 다음부터 훑어 납부·제외된 회차를 건너뛴 첫 회차
  let nextDueKey = addCycleMonths(anchorKey, 1);
  for (
    let i = 0;
    i < MAX_CYCLE_SCAN && (paid.has(nextDueKey) || excluded.has(nextDueKey));
    i += 1
  ) {
    nextDueKey = addCycleMonths(nextDueKey, 1);
  }

  return { anchorKey, collectKey, nextDueKey, status };
}

/** 납부 기록 배열에서 완료한 회차 키 목록을 뽑는다. */
export function paidCycleKeysOf(
  payments: { cycle_due: string }[],
  monthOffset = 0,
): string[] {
  return payments.map((p) => cycleKeyOfDate(p.cycle_due, monthOffset));
}